/**
 * Pure geometry/ordering logic for the experimental windowed ("desktop") UI.
 *
 * No DOM access here so it can be unit-tested: the DesktopManager applies
 * these results to the window frames it renders.
 */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Size {
  w: number;
  h: number;
}

/**
 * Which workspace panels a window shows, plus any user-dragged panel sizes.
 * Each window remembers its own so e.g. a source-editor window and a
 * docs-only window can sit side by side.
 */
export interface PanelState {
  edit: boolean;
  docs: boolean;
  scripts: boolean;
  vars: boolean;
  /** Inline CSS sizes set by the resize handles (`''`/undefined = default). */
  editorWidth?: string;
  scriptsWidth?: string;
  varsHeight?: string;
}

export interface WindowState {
  path: string;
  rect: Rect;
  maximized: boolean;
  minimized: boolean;
  /** Stacking order; higher = in front. */
  z: number;
  /** Per-window panel layout; unset until the window has hosted the workspace. */
  panels?: PanelState;
}

export interface PersistedWindow {
  x: number;
  y: number;
  w: number;
  h: number;
  maximized: boolean;
  minimized: boolean;
  z: number;
  panels?: PanelState;
}

/** Serialized shape stored in localStorage (`leedoc_windows`). */
export interface WindowPersistence {
  windows: Record<string, PersistedWindow>;
}

export type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export const RESIZE_EDGES: ResizeEdge[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

/** Minimum usable window size — the title bar must stay grabbable. */
export const MIN_WINDOW_SIZE: Size = { w: 360, h: 220 };

/** How far each cascaded window is offset from the previous one. */
export const CASCADE_STEP = 32;

/**
 * Default size for a brand-new window on a given desktop: large enough to be
 * useful, but leaving room so the cascade is visible.
 */
export function defaultWindowSize(desktop: Size): Size {
  const w = clamp(Math.round(desktop.w * 0.62), MIN_WINDOW_SIZE.w, Math.max(MIN_WINDOW_SIZE.w, desktop.w - 2 * CASCADE_STEP));
  const h = clamp(Math.round(desktop.h * 0.72), MIN_WINDOW_SIZE.h, Math.max(MIN_WINDOW_SIZE.h, desktop.h - CASCADE_STEP));
  return { w, h };
}

/**
 * Keep a rect inside the desktop. The window may be shrunk to fit; if the
 * desktop is smaller than the minimum size the rect is pinned to the origin
 * and sized to the desktop so the title bar never ends up off-screen.
 */
export function clampToDesktop(rect: Rect, desktop: Size): Rect {
  const w = clamp(rect.w, Math.min(MIN_WINDOW_SIZE.w, desktop.w), Math.max(1, desktop.w));
  const h = clamp(rect.h, Math.min(MIN_WINDOW_SIZE.h, desktop.h), Math.max(1, desktop.h));
  const x = clamp(rect.x, 0, Math.max(0, desktop.w - w));
  const y = clamp(rect.y, 0, Math.max(0, desktop.h - h));
  return { x, y, w, h };
}

/**
 * Pick a cascaded position for a new window: offset from the front-most
 * existing (non-maximized, non-minimized) window, wrapping back to the
 * top-left corner once it would run off the desktop.
 */
export function cascadeRect(existing: WindowState[], desktop: Size, size: Size = defaultWindowSize(desktop)): Rect {
  const candidates = existing.filter(w => !w.maximized && !w.minimized);
  if (candidates.length === 0) {
    return clampToDesktop({ x: CASCADE_STEP, y: CASCADE_STEP / 2, ...size }, desktop);
  }
  const front = candidates.reduce((a, b) => (b.z > a.z ? b : a));
  let x = front.rect.x + CASCADE_STEP;
  let y = front.rect.y + CASCADE_STEP;
  if (x + size.w > desktop.w || y + size.h > desktop.h) {
    x = CASCADE_STEP;
    y = CASCADE_STEP / 2;
  }
  return clampToDesktop({ x, y, ...size }, desktop);
}

/**
 * Lay `count` windows out in a near-square grid that fills the desktop.
 * Returns rects in the order the windows should be assigned.
 */
export function tileRects(count: number, desktop: Size): Rect[] {
  if (count <= 0) return [];
  const cols = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / cols);
  const cellW = Math.floor(desktop.w / cols);
  const cellH = Math.floor(desktop.h / rows);
  const rects: Rect[] = [];
  for (let i = 0; i < count; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    // The last row may have fewer windows — stretch them to fill the width.
    const inLastRow = row === rows - 1;
    const lastRowCount = count - (rows - 1) * cols;
    const w = inLastRow ? Math.floor(desktop.w / lastRowCount) : cellW;
    rects.push({ x: col * w, y: row * cellH, w, h: cellH });
  }
  return rects;
}

/**
 * Cascade every window from the top-left corner in the given order.
 */
export function cascadeRects(count: number, desktop: Size): Rect[] {
  const size = defaultWindowSize(desktop);
  const rects: Rect[] = [];
  let x = CASCADE_STEP;
  let y = CASCADE_STEP / 2;
  for (let i = 0; i < count; i++) {
    if (x + size.w > desktop.w || y + size.h > desktop.h) {
      x = CASCADE_STEP;
      y = CASCADE_STEP / 2;
    }
    rects.push(clampToDesktop({ x, y, ...size }, desktop));
    x += CASCADE_STEP;
    y += CASCADE_STEP;
  }
  return rects;
}

/**
 * Apply a drag delta to one edge/corner of a rect, honouring the minimum
 * size (the opposite edge stays put) and the desktop bounds.
 */
export function resizeRect(start: Rect, edge: ResizeEdge, dx: number, dy: number, desktop: Size): Rect {
  let { x, y, w, h } = start;
  const minW = Math.min(MIN_WINDOW_SIZE.w, desktop.w);
  const minH = Math.min(MIN_WINDOW_SIZE.h, desktop.h);

  if (edge.includes('e')) {
    w = clamp(start.w + dx, minW, desktop.w - start.x);
  }
  if (edge.includes('s')) {
    h = clamp(start.h + dy, minH, desktop.h - start.y);
  }
  if (edge.includes('w')) {
    const right = start.x + start.w;
    const newX = clamp(start.x + dx, 0, right - minW);
    x = newX;
    w = right - newX;
  }
  if (edge.includes('n')) {
    const bottom = start.y + start.h;
    const newY = clamp(start.y + dy, 0, bottom - minH);
    y = newY;
    h = bottom - newY;
  }
  return { x, y, w, h };
}

/** Apply a move delta, keeping the window inside the desktop. */
export function moveRect(start: Rect, dx: number, dy: number, desktop: Size): Rect {
  return clampToDesktop({ ...start, x: start.x + dx, y: start.y + dy }, desktop);
}

/**
 * In-memory store of window states with z-ordering and (de)serialization.
 * The DesktopManager owns one of these and mirrors it to the DOM.
 */
export class WindowLayout {
  private windows = new Map<string, WindowState>();
  private zCounter = 0;

  get all(): WindowState[] {
    return [...this.windows.values()];
  }

  get(path: string): WindowState | null {
    return this.windows.get(path) ?? null;
  }

  has(path: string): boolean {
    return this.windows.has(path);
  }

  /** Paths ordered back-to-front. */
  get stackingOrder(): string[] {
    return this.all.sort((a, b) => a.z - b.z).map(w => w.path);
  }

  /** The front-most visible (non-minimized) window, if any. */
  frontVisible(): WindowState | null {
    const visible = this.all.filter(w => !w.minimized);
    if (visible.length === 0) return null;
    return visible.reduce((a, b) => (b.z > a.z ? b : a));
  }

  /**
   * Create a window for `path`. Position/size come from `rect` when supplied
   * (e.g. restored from storage), otherwise a cascade from the current
   * front-most window. `maximized` defaults to the front window's state so a
   * maximized workflow stays maximized as new files open.
   */
  add(path: string, desktop: Size, opts: { rect?: Rect; maximized?: boolean; minimized?: boolean; z?: number; panels?: PanelState } = {}): WindowState {
    const existing = this.windows.get(path);
    if (existing) return existing;
    const front = this.frontVisible();
    const maximized = opts.maximized ?? (front ? front.maximized : false);
    const rect = opts.rect ? clampToDesktop(opts.rect, desktop) : cascadeRect(this.all, desktop);
    const z = opts.z ?? ++this.zCounter;
    if (z > this.zCounter) this.zCounter = z;
    const state: WindowState = { path, rect, maximized, minimized: opts.minimized ?? false, z };
    if (opts.panels) state.panels = { ...opts.panels };
    this.windows.set(path, state);
    return state;
  }

  remove(path: string): boolean {
    return this.windows.delete(path);
  }

  rename(oldPath: string, newPath: string): boolean {
    const state = this.windows.get(oldPath);
    if (!state || this.windows.has(newPath)) return false;
    this.windows.delete(oldPath);
    state.path = newPath;
    this.windows.set(newPath, state);
    return true;
  }

  bringToFront(path: string): WindowState | null {
    const state = this.windows.get(path);
    if (!state) return null;
    if (state.z !== this.zCounter) state.z = ++this.zCounter;
    return state;
  }

  setRect(path: string, rect: Rect, desktop: Size): void {
    const state = this.windows.get(path);
    if (!state) return;
    state.rect = clampToDesktop(rect, desktop);
  }

  setMaximized(path: string, maximized: boolean): void {
    const state = this.windows.get(path);
    if (state) state.maximized = maximized;
  }

  setMinimized(path: string, minimized: boolean): void {
    const state = this.windows.get(path);
    if (state) state.minimized = minimized;
  }

  setPanels(path: string, panels: PanelState): void {
    const state = this.windows.get(path);
    if (state) state.panels = { ...panels };
  }

  /** Re-clamp every window after the desktop itself changed size. */
  fitAll(desktop: Size): void {
    for (const w of this.windows.values()) {
      w.rect = clampToDesktop(w.rect, desktop);
    }
  }

  /** Tile all non-minimized windows (back-to-front order) across the desktop. */
  tileAll(desktop: Size): void {
    const paths = this.stackingOrder.filter(p => !this.windows.get(p)!.minimized);
    const rects = tileRects(paths.length, desktop);
    paths.forEach((p, i) => {
      const w = this.windows.get(p)!;
      w.rect = rects[i];
      w.maximized = false;
    });
  }

  /** Cascade all non-minimized windows (back-to-front order). */
  cascadeAll(desktop: Size): void {
    const paths = this.stackingOrder.filter(p => !this.windows.get(p)!.minimized);
    const rects = cascadeRects(paths.length, desktop);
    paths.forEach((p, i) => {
      const w = this.windows.get(p)!;
      w.rect = rects[i];
      w.maximized = false;
    });
  }

  serialize(): WindowPersistence {
    const windows: WindowPersistence['windows'] = {};
    for (const w of this.windows.values()) {
      windows[w.path] = { ...w.rect, maximized: w.maximized, minimized: w.minimized, z: w.z };
      if (w.panels) windows[w.path].panels = { ...w.panels };
    }
    return { windows };
  }

  /**
   * Restore geometry for known paths. Only paths present in `paths` are
   * restored (closed tabs are dropped); stacking is normalized to 1..n.
   */
  restore(data: WindowPersistence | null | undefined, paths: string[], desktop: Size): void {
    if (!data || typeof data !== 'object' || !data.windows) return;
    const entries = paths
      .map(p => [p, data.windows[p]] as const)
      .filter((e): e is readonly [string, PersistedWindow] => !!e[1] && isFiniteRect(e[1]))
      .sort((a, b) => (a[1].z ?? 0) - (b[1].z ?? 0));
    for (const [path, w] of entries) {
      this.add(path, desktop, {
        rect: { x: w.x, y: w.y, w: w.w, h: w.h },
        maximized: !!w.maximized,
        minimized: !!w.minimized,
        panels: isPanelState(w.panels) ? w.panels : undefined,
      });
    }
  }
}

function isPanelState(p: unknown): p is PanelState {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return ['edit', 'docs', 'scripts', 'vars'].every(k => typeof o[k] === 'boolean');
}

function isFiniteRect(r: { x: number; y: number; w: number; h: number }): boolean {
  return [r.x, r.y, r.w, r.h].every(n => typeof n === 'number' && Number.isFinite(n));
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}
